import React, { useCallback, useContext, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'

import config from 'constants/config'
import BedbankRoomOption from './BedbankRoomOption'
import { OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'

import { sortBy } from 'lib/array/arrayUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

import { addToCheckoutCart } from 'actions/CheckoutActions'
import { accountAccessShowLogin } from 'actions/UiActions'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import useQueryParams from 'hooks/useQueryParams'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { isLoggedIn } from 'selectors/accountSelectors'
import { generateBedbankAccommodationCheckoutItems } from 'lib/checkout/accommodation/cart'
import { dateDifference } from 'lib/datetime/dateUtils'
import { enquiry } from 'api/bedbank'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useCreateTripItemBatch, useTrip } from 'tripPlanner/hooks/api'
import { createBedbankHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/bedbankHotel'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { FlightViewTypes } from 'constants/flight'
import { getOfferExtra } from 'selectors/offerExtraSelector'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { scrollToOfferPackageOptions, scrollToOfferPackageOptionsOfferType } from 'analytics/optimizely/events'
import { InView } from 'react-intersection-observer'
import BedbankOfferFlightDealBanner from './BedbankOfferFlightDealBanner'

const GOOGLE_ADS_UTM_CAMPAIGN = 'google-hotel-ads'

interface Props {
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  className?: string;
  offer: App.BedbankOffer;
  rooms: Array<App.Occupants>;
  cheapestFlightPrice?: number;
  flightsEnabled: boolean;
  duration: number;
  activeFilter?: OfferPageFilterTypes;
  onUserAnalyticsAction?: (action: string, label?: string) => void;
  isLoggedIn: boolean;
  region: string;
  currency: string;
  auth: App.AuthState;
  tripPlannerTripId?: string;
  isCalendarEnabled: boolean;
  offerExtra: App.OfferExtra,
  onViewPackageOptions: () => void;
}

function BedbankRoomOptions(props: Props) {
  const {
    auth,
    region,
    currency,
    checkinDate,
    checkoutDate,
    className,
    offer,
    rooms,
    flightsEnabled,
    activeFilter,
    isLoggedIn,
    tripPlannerTripId,
    duration,
    isCalendarEnabled,
    cheapestFlightPrice,
    offerExtra,
    onViewPackageOptions,
  } = props

  const dispatch = useAppDispatch()
  const query = useQueryParams()

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const [packageViews, fetchingRates] = useBedbankPackageViews(offer, region, currency, rooms, duration, checkinDate, checkoutDate)
  const offerPageState = useContext(OfferPageStateContext)
  const validCheckinCheckoutDates = !!(checkinDate && checkoutDate)
  const roomTypeId = query.get('roomTypeId')
  const utmCampaign = query.get('utm_campaign')

  const hasPromotions = offer.promotions.length > 0
  const showAll = packageViews.filter(p => p.available).length === 0

  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews, validCheckinCheckoutDates, hasPromotions])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmCampaign === GOOGLE_ADS_UTM_CAMPAIGN && highlightedPackage) {
      return [highlightedPackage, sortedPackageViews.find(p => p.package.roomId !== roomTypeId)]
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmCampaign, roomTypeId])

  const bookedTodayByRoom = useMemo(() => {
    return offerExtra?.roomsPurchasesToday?.reduce((acc, room) => {
      acc[room.roomTypeId] = room.purchases
      return acc
    }, {}) || {}
  }, [offerExtra])

  const handlePackageOptionsInView = useCallback((inView: boolean) => {
    if (inView) {
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptions())
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptionsOfferType(offer.type))
    }
  }, [offer])

  const proceedToCheckout = useCallback(
    async(
      pkg: App.BedbankPackage,
      rate: App.BedbankRate,
      flights: boolean,
      flightPrice?: number,
    ) => {
      const dates = {
        checkIn: checkinDate.format(ISO_DATE_FORMAT),
        checkOut: checkoutDate.format(ISO_DATE_FORMAT),
        duration: dateDifference(checkoutDate.toDate(), checkinDate.toDate()).days,
      }

      // Initiate a session
      const session = await enquiry({
        propertyId: offer.id,
        roomTypeId: rate.roomId,
        roomRateId: rate.id,
        isFlightBundle: rate.isFlightBundle,
        csBooking: auth.account.isSpoofed,
        region,
        rooms,
        checkIn: dates.checkIn,
        checkOut: dates.checkOut,
        price: rate.totals.inclusive,
      })

      const accommodationItems = generateBedbankAccommodationCheckoutItems(offer, session.sessionId, pkg, rate, dates, rooms)
      const flightItems: Array<App.Checkout.FlightItem> = []
      if (flights && offerPageState.airport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: offerPageState.airport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: (flightPrice ?? 0) / rooms.length,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }
      dispatch(addToCheckoutCart([...accommodationItems, ...flightItems]))
      fireEcommerceGTMEvent(addToCartGTM(offer, currency, session?.price, sumUpOccupancies(rooms), 'hotel'))
      dispatch(navigateToCheckout())
      Analytics.track('add-to-cart', addToCart())
    }, [checkinDate, checkoutDate, offer, auth.account.isSpoofed, region, rooms, offerPageState.airport, dispatch, currency])

  const onBook = useCallback((pkg: App.BedbankPackage, rate: App.BedbankRate, withFlights?: boolean, flightPrice?: number) => {
    trackEvent('BOOK_NOW_PRESSED')
    if (!isLoggedIn) { return dispatch(accountAccessShowLogin()) }

    if (currentTrip) {
      const payload = createBedbankHotelItemPayload({
        offer,
        pkg,
        rate,
        rooms,
        checkIn: checkinDate.format(ISO_DATE_FORMAT),
        checkOut: checkoutDate.format(ISO_DATE_FORMAT),
        currentRegionCode: region,
        currencyCode: currency,
      })
      createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
    }

    proceedToCheckout(pkg, rate, withFlights, flightPrice)
  }, [
    isLoggedIn, currentTrip, offer, dispatch, rooms, checkinDate, trackEvent,
    checkoutDate, region, currency, createTripItems, proceedToCheckout])

  const showOfferBanner = offer.flights.airportCode && offerPageState.airport?.code && !fetchingRates

  return <LayoutContainer className={className}>
    <VerticalSpacer gap={16}>
      {!isCalendarEnabled &&
        <BodyText variant="large" align="center" colour="neutral-one">
          Select your {config.brandConfig.travelSynonym} options
        </BodyText>}

      {showOfferBanner && <BedbankOfferFlightDealBanner
        destinationAirportCode={offer.flights.airportCode!}
        originAirportCode={offerPageState.airport?.code!}
        packageView={sortedPackages[0]}
      />}

      {sortedPackages.filter(packageView => (packageView.show && packageView.available) || (packageView.show && showAll)).map((packageView, index) =>
        <div key={packageView.package.roomId}>
          <BedbankRoomOption
            isFilterActive={!!activeFilter}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            offer={offer}
            packageView={packageView}
            rooms={rooms}
            onBook={onBook}
            fetchingRates={fetchingRates}
            flightPrice={cheapestFlightPrice}
            flightsEnabled={flightsEnabled}
            bookedToday={bookedTodayByRoom[packageView.package.roomId]}
            isHighlighted={utmCampaign === GOOGLE_ADS_UTM_CAMPAIGN && roomTypeId === packageView.package.roomId}
            onViewPackageOptions={onViewPackageOptions}
            flightOrigin={offerPageState.airport}
          />
          {index === 0 &&
            <InView rootMargin="0px" triggerOnce onChange={handlePackageOptionsInView} />
          }
        </div>)
      }
    </VerticalSpacer>
  </LayoutContainer>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    auth: state.auth,
    currency: state.geo.currentCurrency,
    region: state.geo.currentRegionCode,
    isLoggedIn: isLoggedIn(state),
    tripPlannerTripId: state.tripPlanner.currentTripId,
    isCalendarEnabled: isFlashCalendarEnabled({ bedbankOffer: ownProps.offer }, state),
    offerExtra: getOfferExtra(state, ownProps.offer?.id),
  }
}

export default connect(mapStateToProps)(BedbankRoomOptions)
